import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchTradingCostCenters,
    fetchTradingItems,
    fetchTradingItemTempDetails,
    fetchTradingItemsGrid,
    addTempTradingItems,
    removeTradingStockItem,
    fetchTradingClients,
    fetchTradingSubClients,
    fetchTradingSubClientPO,
    fetchTradingClientGSTNos,
    fetchTradingCompanyGSTNos,
    fetchTradingTCSDCASDCAS,
    submitTradingInvoice,
    clearTradingSaveResult,
    clearTradingTempDetails,
    clearTradingItemsGrid,
    clearTradingSubClients,
    resetTradingInvoice,
    selectTradingCostCenters,
    selectTradingItems,
    selectTradingTempDetails,
    selectTradingItemsGrid,
    selectTradingClients,
    selectTradingSubClients,
    selectTradingPONumbers,
    selectTradingClientGSTNos,
    selectTradingCompanyGSTNos,
    selectTradingTCSDCASDCAS,
    selectTradingSaveResult,
    selectTradingCCLoading,
    selectTradingItemsLoading,
    selectTradingTempLoading,
    selectTradingGridLoading,
    selectTradingClientsLoading,
    selectTradingSubCliLoading,
    selectTradingPOLoading,
    selectTradingGSTLoading,
    selectTradingSaveLoading,
    selectTradingTempSaveLoading,
    selectTradingDeleteLoading,
    selectTradingSaveError,
} from '../../slices/accountsSlice/clientTradingInvoiceSlice';

import {
    ReceiptText, IndianRupee,
    ShieldCheck, Percent, CheckCircle,
    Loader2, ChevronDown, RotateCcw, Send,
    ShoppingCart, Plus, Trash2, Package,
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const INVOICE_TYPES = [
    { value: 'Invoice Service', label: 'Invoice Service' },
    { value: 'Manufacturing',   label: 'Manufacturing'   },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const fmt = (v) => {
    const n = parseFloat(v);
    if ((!v && v !== 0) || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Per-row computed values
const rowAmount  = (r) => (parseFloat(r.qty) || 0) * (parseFloat(r.rate) || 0);
const rowCgstAmt = (r) => rowAmount(r) * (parseFloat(r.cgstPer) || 0) / 100;
const rowSgstAmt = (r) => rowAmount(r) * (parseFloat(r.sgstPer) || 0) / 100;
const rowIgstAmt = (r) => rowAmount(r) * (parseFloat(r.igstPer) || 0) / 100;

const buildTaxStrings = (form) => {
    if (form.IsGstApplicable !== 'Yes') return { Taxtypes: null, Taxdcas: null, Taxamounts: null };
    if (form.Statecheck) {
        const cgstTotal = parseFloat(form.Cgstsdcaamt) || 0;
        const sgstTotal = parseFloat(form.Sgstsdcaamt) || 0;
        return {
            Taxtypes:   'CGST,SGST',
            Taxdcas:    `${form.CgstDca},${form.SgstDca}`,
            Taxamounts: `${cgstTotal},${sgstTotal}`,
        };
    }
    return {
        Taxtypes:   'IGST',
        Taxdcas:    form.IgstDca,
        Taxamounts: String(parseFloat(form.Igstsdcaamt) || 0),
    };
};

// ── Shared UI primitives (compact) ────────────────────────────────────────────
const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const cellInput = (extraCls = '') =>
    `w-full px-2 py-0.5 text-right rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:border-indigo-400 ${extraCls}`;

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

const RupeeInput = ({ value, onChange, disabled }) => (
    <div className="relative">
        <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input type="number" placeholder="0.00" value={value} onChange={onChange} disabled={disabled} className={`${inputCls} pl-8`} />
    </div>
);

// Small inline Yes/No segmented toggle
const YesNoToggle = ({ value, onChange }) => (
    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
        {['Yes', 'No'].map(opt => (
            <button
                key={opt}
                type="button"
                onClick={() => onChange(opt)}
                className={`px-4 py-1 text-xs font-semibold transition-all ${
                    value === opt
                        ? opt === 'Yes'
                            ? 'bg-green-500 text-white'
                            : 'bg-rose-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
                {opt}
            </button>
        ))}
    </div>
);

// ── Initial state ──────────────────────────────────────────────────────────────
const INITIAL_FORM = {
    InvoiceType:       '',
    CCCode:            '',
    RANO:              '',
    Clientcode:        '',
    SubClientcode:     '',
    PONumber:          '',
    ClientInvoiceNo:   '',
    InvoiceDate:       '',
    InvoiceMakingDate: '',
    InvoiceRemarks:    '',
    IsGstApplicable:   '',
    TaxId:             0,
    ClientGST:         '',
    CompanyGST:        '',
    Statecheck:        false,
    CgstDca:           '',
    Cgstsdca:          '',
    SgstDca:           '',
    Sgstsdca:          '',
    IgstDca:           '',
    Igstsdca:          '',
    IsTCSApplicable:   '',
    TCSDCA:            '',
    TCSSDCA:           '',
    TCSAmount:         '',
};

// ── Component ─────────────────────────────────────────────────────────────────
const ClientTradingInvoiceCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const roleId = userData?.roleId  || userData?.RID  || 0;
    const userId = userData?.userId  || userData?.UID  || userData?.employeeId || '';

    // Redux state
    const costCenters   = useSelector(selectTradingCostCenters);
    const tradeItems    = useSelector(selectTradingItems);
    const tempDetails   = useSelector(selectTradingTempDetails);
    const itemsGrid     = useSelector(selectTradingItemsGrid);
    const clients       = useSelector(selectTradingClients);
    const subClients    = useSelector(selectTradingSubClients);
    const poNumbers     = useSelector(selectTradingPONumbers);
    const clientGSTNos  = useSelector(selectTradingClientGSTNos);
    const companyGSTNos = useSelector(selectTradingCompanyGSTNos);
    const tcsDCASDCAS   = useSelector(selectTradingTCSDCASDCAS);
    const saveResult    = useSelector(selectTradingSaveResult);

    const ccLoading       = useSelector(selectTradingCCLoading);
    const itemsLoading    = useSelector(selectTradingItemsLoading);
    const tempLoading     = useSelector(selectTradingTempLoading);
    const gridLoading     = useSelector(selectTradingGridLoading);
    const clientsLoading  = useSelector(selectTradingClientsLoading);
    const subCliLoading   = useSelector(selectTradingSubCliLoading);
    const poLoading       = useSelector(selectTradingPOLoading);
    const gstLoading      = useSelector(selectTradingGSTLoading);
    const saveLoading     = useSelector(selectTradingSaveLoading);
    const tempSaveLoading = useSelector(selectTradingTempSaveLoading);
    const deleteLoading   = useSelector(selectTradingDeleteLoading);
    const saveError       = useSelector(selectTradingSaveError);

    // Local state
    const [form, setForm]         = useState(INITIAL_FORM);
    const [tradeRows, setTradeRows] = useState([]); // items added to invoice — { id, itemCode, itemName, spec, unit, qty, rate, hsn, cgstPer, sgstPer, igstPer }
    const [selItem, setSelItem]   = useState('');
    const [tempQtys, setTempQtys] = useState({});

    // Derived totals from items
    const basicValue  = tradeRows.reduce((s, r) => s + rowAmount(r), 0);
    const totalCgst   = tradeRows.reduce((s, r) => s + rowCgstAmt(r), 0);
    const totalSgst   = tradeRows.reduce((s, r) => s + rowSgstAmt(r), 0);
    const totalIgst   = tradeRows.reduce((s, r) => s + rowIgstAmt(r), 0);
    const invoiceTotal = basicValue
        + (form.IsGstApplicable === 'Yes' ? (form.Statecheck ? totalCgst + totalSgst : totalIgst) : 0)
        + (form.IsTCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0);

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchTradingCompanyGSTNos('GST'));
        dispatch(fetchTradingTCSDCASDCAS());
        return () => { dispatch(resetTradingInvoice()); };
    }, [dispatch]);

    useEffect(() => {
        if (tcsDCASDCAS) {
            setForm(p => ({
                ...p,
                TCSDCA:  p.TCSDCA  || tcsDCASDCAS.TCSDCA  || '',
                TCSSDCA: p.TCSSDCA || tcsDCASDCAS.TCSSDCA || '',
            }));
        }
    }, [tcsDCASDCAS]);

    // Auto-detect GST state from GST number prefixes
    useEffect(() => {
        if (form.ClientGST && form.CompanyGST) {
            const cState = form.ClientGST.replace(/\s.*/, '').substring(0, 2);
            const coState = form.CompanyGST.replace(/\s.*/, '').substring(0, 2);
            setForm(p => ({ ...p, Statecheck: cState === coState }));
        }
    }, [form.ClientGST, form.CompanyGST]);

    // Fetch grid whenever CC is set (loads previously added items)
    useEffect(() => {
        if (form.CCCode) dispatch(fetchTradingItemsGrid(form.CCCode));
    }, [form.CCCode]);

    // When itemsGrid loads, enrich existing tradeRows with server data (name, spec, HSN, GST%)
    // and add any rows from grid not already in local state (for CC-change restore)
    useEffect(() => {
        if (!Array.isArray(itemsGrid) || itemsGrid.length === 0) return;
        setTradeRows(prev => {
            // Build a map of existing local rows by itemCode
            const localMap = {};
            prev.forEach(r => { localMap[r.itemCode] = r; });

            // Build enriched rows from grid data
            const gridMap = {};
            const gridRows = itemsGrid.map(r => {
                const local = localMap[r.OItemcode];
                gridMap[r.OItemcode] = true;
                return {
                    id:       local?.id ?? (Date.now() + Math.random()),
                    itemCode: r.OItemcode || '',
                    itemName: r.OItemname || local?.itemName || '',
                    spec:     r.OItemspecification || local?.spec || '',
                    unit:     r.OUnits || local?.unit || '',
                    qty:      local?.qty ?? parseFloat(r.OItemqty || 0),
                    rate:     local?.rate    ?? parseFloat(r.OSellingBasicprice || 0),
                    hsn:      local?.hsn     ?? (r.OHSN     || ''),
                    cgstPer:  local?.cgstPer ?? (r.Ocgstper || ''),
                    sgstPer:  local?.sgstPer ?? (r.Osgstper || ''),
                    igstPer:  local?.igstPer ?? (r.OIgstper || ''),
                };
            });

            // Keep local rows not yet returned by grid (newly added, pending server)
            const localOnly = prev.filter(r => !gridMap[r.itemCode]);
            return [...gridRows, ...localOnly];
        });
    }, [itemsGrid]);

    // Handle save result
    useEffect(() => {
        if (!saveResult) return;
        const msg = typeof saveResult === 'string' ? saveResult : saveResult?.Message || '';
        if (msg === 'Submited' || msg.toLowerCase().includes('success')) {
            toast.success('Trading invoice submitted successfully!');
            handleReset();
        } else if (msg) {
            toast.error(msg);
        }
        dispatch(clearTradingSaveResult());
    }, [saveResult]);

    useEffect(() => {
        if (saveError) toast.error(saveError);
    }, [saveError]);

    // ── Cascade Handlers ──────────────────────────────────────────────────────
    const handleInvoiceTypeChange = (val) => {
        setForm(p => ({ ...INITIAL_FORM, InvoiceType: val, TCSDCA: p.TCSDCA, TCSSDCA: p.TCSSDCA }));
        setTradeRows([]);
        setSelItem('');
        setTempQtys({});
        dispatch(clearTradingItemsGrid());
        if (val) dispatch(fetchTradingCostCenters(val));
    };

    const handleCCChange = (ccCode) => {
        setForm(p => ({ ...INITIAL_FORM, InvoiceType: p.InvoiceType, CCCode: ccCode, TCSDCA: p.TCSDCA, TCSSDCA: p.TCSSDCA }));
        setTradeRows([]);
        setSelItem('');
        setTempQtys({});
        dispatch(clearTradingTempDetails());
        dispatch(clearTradingSubClients());
        if (ccCode) {
            dispatch(fetchTradingItems(ccCode));
            dispatch(fetchTradingClients(ccCode));
        }
    };

    const handleClientChange = (clientcode) => {
        setForm(p => ({ ...p, Clientcode: clientcode, SubClientcode: '', PONumber: '' }));
        dispatch(clearTradingSubClients());
        if (clientcode && form.CCCode) {
            dispatch(fetchTradingSubClients({ ccCode: form.CCCode, clientcode }));
        }
    };

    const handleSubClientChange = (subClientcode) => {
        setForm(p => ({ ...p, SubClientcode: subClientcode, PONumber: '' }));
        if (subClientcode && form.CCCode && form.Clientcode) {
            dispatch(fetchTradingSubClientPO({ subClient: subClientcode, ccCode: form.CCCode, clientcode: form.Clientcode }));
            dispatch(fetchTradingClientGSTNos({ taxtype: 'SubClient', taxfor: subClientcode }));
        }
    };

    // ── Item Handlers ─────────────────────────────────────────────────────────
    const handleItemSelect = (itemcode) => {
        setSelItem(itemcode);
        setTempQtys({});
        dispatch(clearTradingTempDetails());
        if (itemcode && form.CCCode) {
            dispatch(fetchTradingItemTempDetails({ itemcode, ccCode: form.CCCode }));
        }
    };

    const handleAddToInvoice = () => {
        if (!selItem) { toast.warn('Select a trade item'); return; }
        if (!tempDetails.length) { toast.warn('No temp details available for this item'); return; }

        const rowsWithQty = tempDetails.filter(r => parseFloat(tempQtys[r.TempId] || 0) > 0);
        if (!rowsWithQty.length) { toast.warn('Enter issue quantity for at least one row'); return; }

        const rowids   = tempDetails.map(r => r.TempId || '').map((v) => `${v},`).join('');
        const codes    = tempDetails.map(r => r.TempItemcode || selItem).map((v) => `${v},`).join('');
        const qtys     = tempDetails.map(r => tempQtys[r.TempId] || '0').map((v) => `${v},`).join('');
        const tblnames = tempDetails.map(r => r.Tblname || '').map((v) => `${v},`).join('');

        dispatch(addTempTradingItems({
            Trowids:         rowids,
            TItemCodes:      codes,
            TNewQtys:        qtys,
            Ttblnames:       tblnames,
            TCCCode:         form.CCCode,
            TSingleItemcode: selItem,
        }))
            .unwrap()
            .then(() => {
                // Add row immediately to state so it shows without waiting for grid re-fetch
                const itemMeta = tradeItems.find(i => (i.ItemCode || i.Itemcode) === selItem);
                const totalQty = rowsWithQty.reduce((s, r) => s + parseFloat(tempQtys[r.TempId] || 0), 0);
                setTradeRows(prev => {
                    const exists = prev.findIndex(r => r.itemCode === selItem);
                    if (exists >= 0) {
                        // Merge qty if item already in table
                        return prev.map((r, i) => i === exists ? { ...r, qty: r.qty + totalQty } : r);
                    }
                    return [...prev, {
                        id:       Date.now() + Math.random(),
                        itemCode: selItem,
                        itemName: itemMeta?.ItemName || itemMeta?.Itemname || selItem,
                        spec:     itemMeta?.Specification || itemMeta?.specification || '',
                        unit:     itemMeta?.Unit || itemMeta?.unit || '',
                        qty:      totalQty,
                        rate:     0,
                        hsn:      '',
                        cgstPer:  '',
                        sgstPer:  '',
                        igstPer:  '',
                    }];
                });
                setSelItem('');
                setTempQtys({});
                dispatch(clearTradingTempDetails());
                // Also re-fetch grid to enrich rows with server data (spec, HSN, GST %)
                dispatch(fetchTradingItemsGrid(form.CCCode));
            })
            .catch(err => toast.error(err || 'Failed to add item'));
    };

    const updateRow = (id, field, value) => {
        setTradeRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    };

    const handleRemoveRow = (row) => {
        dispatch(removeTradingStockItem({ DItemcode: row.itemCode, DCCCode: form.CCCode }))
            .unwrap()
            .then(() => setTradeRows(prev => prev.filter(r => r.id !== row.id)))
            .catch(err => toast.error(err || 'Failed to remove item'));
    };

    // ── Reset ─────────────────────────────────────────────────────────────────
    const handleReset = () => {
        setForm(p => ({ ...INITIAL_FORM, TCSDCA: tcsDCASDCAS?.TCSDCA || '', TCSSDCA: tcsDCASDCAS?.TCSSDCA || '' }));
        setTradeRows([]);
        setSelItem('');
        setTempQtys({});
        dispatch(clearTradingTempDetails());
        dispatch(clearTradingItemsGrid());
        dispatch(fetchTradingCompanyGSTNos('GST'));
        dispatch(fetchTradingTCSDCASDCAS());
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!form.InvoiceType)      { toast.warn('Select an Invoice Type');        return; }
        if (!form.CCCode)           { toast.warn('Select a Cost Center');          return; }
        if (tradeRows.length === 0) { toast.warn('Add at least one item');         return; }
        if (!form.Clientcode)       { toast.warn('Select a Client');               return; }
        if (!form.SubClientcode)    { toast.warn('Select a Sub-Client');           return; }
        if (!form.PONumber)         { toast.warn('Select a PO Number');            return; }
        if (!form.ClientInvoiceNo)  { toast.warn('Enter Client Invoice Number');   return; }
        if (!form.InvoiceDate)      { toast.warn('Select Invoice Date');           return; }
        if (!form.InvoiceMakingDate){ toast.warn('Select Invoice Making Date');    return; }
        if (!form.IsGstApplicable)  { toast.warn('Specify if GST is applicable');  return; }
        if (form.IsGstApplicable === 'Yes') {
            if (!form.ClientGST)  { toast.warn('Select Client GST Number');  return; }
            if (!form.CompanyGST) { toast.warn('Select Company GST Number'); return; }
        }
        if (!form.IsTCSApplicable)  { toast.warn('Specify if TCS is applicable'); return; }

        const taxStrings = buildTaxStrings(form);

        dispatch(submitTradingInvoice({
            PONumber:          form.PONumber,
            RANO:              form.RANO || null,
            CCCode:            form.CCCode,
            ClientInvoiceNo:   form.ClientInvoiceNo,
            InvoiceDate:       formatDateForAPI(form.InvoiceDate),
            InvoiceMakingDate: formatDateForAPI(form.InvoiceMakingDate),
            BasicValue:        basicValue,
            Total:             invoiceTotal,
            InvoiceRemarks:    form.InvoiceRemarks || null,
            InvoiceType:       form.InvoiceType,
            Clientcode:        form.Clientcode,
            SubClientcode:     form.SubClientcode,
            CreatedBy:         userData?.userName || "",
            Roleid:            roleId,
            Taxtypes:          taxStrings.Taxtypes,
            Taxdcas:           taxStrings.Taxdcas,
            Taxamounts:        taxStrings.Taxamounts,
            TaxId:             form.TaxId || 0,
            ClientGST:         form.IsGstApplicable === 'Yes' ? form.ClientGST  : null,
            CompanyGST:        form.IsGstApplicable === 'Yes' ? form.CompanyGST : null,
            Statecheck:        form.IsGstApplicable === 'Yes' ? form.Statecheck  : false,
            Cgstsdca:          form.Cgstsdca   || null,
            Cgstsdcaamt:       form.IsGstApplicable === 'Yes' && form.Statecheck ? totalCgst : 0,
            Sgstsdca:          form.Sgstsdca   || null,
            Sgstsdcaamt:       form.IsGstApplicable === 'Yes' && form.Statecheck ? totalSgst : 0,
            Igstsdca:          form.Igstsdca   || null,
            Igstsdcaamt:       form.IsGstApplicable === 'Yes' && !form.Statecheck ? totalIgst : 0,
            IsGstApplicable:   form.IsGstApplicable || null,
            IsTCSApplicable:   form.IsTCSApplicable || null,
            TCSDCA:            form.IsTCSApplicable === 'Yes' ? form.TCSDCA  : null,
            TCSSDCA:           form.IsTCSApplicable === 'Yes' ? form.TCSSDCA : null,
            TCSAmount:         form.IsTCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0,
            // Per-item arrays as comma-separated strings
            ItemCodes:      tradeRows.map(r => r.itemCode).map((v) => `${v},`).join(''),
            Quantitys:      tradeRows.map(r => r.qty).map((v) => `${v},`).join(''),
            BasicSellings:  tradeRows.map(r => parseFloat(r.rate) || 0).map((v) => `${v},`).join(''),
            SellingAmounts: tradeRows.map(r => rowAmount(r)).map((v) => `${v},`).join(''),
            cgstper:        tradeRows.map(r => parseFloat(r.cgstPer) || 0).map((v) => `${v},`).join(''),
            cgstamt:        tradeRows.map(r => rowCgstAmt(r)).map((v) => `${v},`).join(''),
            sgstper:        tradeRows.map(r => parseFloat(r.sgstPer) || 0).map((v) => `${v},`).join(''),
            sgstamt:        tradeRows.map(r => rowSgstAmt(r)).map((v) => `${v},`).join(''),
            Igstper:        tradeRows.map(r => parseFloat(r.igstPer) || 0).map((v) => `${v},`).join(''),
            Igstamt:        tradeRows.map(r => rowIgstAmt(r)).map((v) => `${v},`).join(''),
            HSN:            tradeRows.map(r => r.hsn || '').map((v) => `${v},`).join(''),
        }));
    };

    // ── Derived option lists ──────────────────────────────────────────────────
    const ccOptions         = costCenters.map(c => ({ value: c.CC_Code || c.CCCode, label: c.CC_Name || c.CCName }));
    const itemOptions       = tradeItems.map(i => ({ value: i.ItemCode || i.Itemcode, label: `${i.ItemCode || i.Itemcode} - ${i.ItemName || i.Itemname || ''}` }));
    const clientOptions     = clients.map(c => ({ value: c.ClientCode || c.Clientcode, label: c.ClientName || c.Clientname }));
    const subClientOptions  = subClients.map(c => ({
        value: c.SubClientCode?.split(',')[0]?.trim() || c.SubClientcode || c.SubClientCode,
        label: c.SubClientCode || c.SubClientcode,
    }));
    const poOptions         = poNumbers.map(p => ({ value: p.PONumber, label: p.PONumber }));
    const clientGSTOptions  = clientGSTNos.map(g => ({ value: g.TaxNoName, label: g.TaxNoName, taxId: g.TaxNoID }));
    const companyGSTOptions = companyGSTNos.map(g => ({ value: g.GSTNo || g.TaxNoName, label: g.TaxNoName }));


    const isBusy      = saveLoading;
    const gstDetected = form.ClientGST && form.CompanyGST;

    // Breakdown for the summary panel — mirrors invoiceTotal
    const gstAmt = form.IsGstApplicable === 'Yes' ? (form.Statecheck ? totalCgst + totalSgst : totalIgst) : 0;
    const tcsAmt = form.IsTCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0;
    const clientLabel = clientOptions.find(c => c.value === form.Clientcode)?.label || form.Clientcode;

    const thCls = 'px-2 py-2 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase';
    const tdCls = 'px-2 py-1.5';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <ShoppingCart className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Client Trading Invoice'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Create trading invoice with per-item GST and HSN</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: form ───────────────────────────────────────────── */}
                <div className="lg:col-span-2 space-y-4">

                    {/* Invoice details — setup, client/PO, dates, remarks */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={ReceiptText} title="Invoice Details" />
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                            <div>
                                <Label required>Invoice Type</Label>
                                <div className="relative">
                                    <select value={form.InvoiceType} onChange={e => handleInvoiceTypeChange(e.target.value)}
                                        disabled={isBusy} className={`${inputCls} appearance-none pr-8`}>
                                        <option value="">— Select Type —</option>
                                        {INVOICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select value={form.CCCode} onChange={e => handleCCChange(e.target.value)}
                                        disabled={!form.InvoiceType || ccLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                        <option value="">{!form.InvoiceType ? 'Select type first' : ccLoading ? 'Loading…' : '— Select CC —'}</option>
                                        {ccOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <Label>RANO</Label>
                                <input type="text" placeholder="Enter RANO reference…" value={form.RANO}
                                    onChange={e => setForm(p => ({ ...p, RANO: e.target.value }))}
                                    disabled={isBusy} className={inputCls} />
                            </div>
                            <div>
                                <Label required>Client</Label>
                                <div className="relative">
                                    <select value={form.Clientcode} onChange={e => handleClientChange(e.target.value)}
                                        disabled={!form.CCCode || clientsLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                        <option value="">{!form.CCCode ? 'Select CC first' : clientsLoading ? 'Loading…' : '— Select Client —'}</option>
                                        {clientOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                    <SelectIcon loading={clientsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Sub Client</Label>
                                <div className="relative">
                                    <select value={form.SubClientcode} onChange={e => handleSubClientChange(e.target.value)}
                                        disabled={!form.Clientcode || subCliLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                        <option value="">{!form.Clientcode ? 'Select client first' : subCliLoading ? 'Loading…' : '— Select Sub Client —'}</option>
                                        {subClientOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                    <SelectIcon loading={subCliLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>PO Number</Label>
                                <div className="relative">
                                    <select value={form.PONumber} onChange={e => setForm(p => ({ ...p, PONumber: e.target.value }))}
                                        disabled={!form.SubClientcode || poLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                        <option value="">{!form.SubClientcode ? 'Select sub-client first' : poLoading ? 'Loading…' : '— Select PO —'}</option>
                                        {poOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                    <SelectIcon loading={poLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Client Invoice No.</Label>
                                <input type="text" placeholder="Enter invoice number…" value={form.ClientInvoiceNo}
                                    onChange={e => setForm(p => ({ ...p, ClientInvoiceNo: e.target.value }))}
                                    disabled={isBusy} className={inputCls} />
                            </div>
                            <div>
                                <Label required>Invoice Date</Label>
                                <CustomDatePicker value={form.InvoiceDate} onChange={val => setForm(p => ({ ...p, InvoiceDate: val }))}
                                    placeholder="Select invoice date" disabled={isBusy} />
                            </div>
                            <div>
                                <Label required>Invoice Making Date</Label>
                                <CustomDatePicker value={form.InvoiceMakingDate} onChange={val => setForm(p => ({ ...p, InvoiceMakingDate: val }))}
                                    placeholder="Select making date" disabled={isBusy} />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-3">
                                <Label>Invoice Remarks</Label>
                                <textarea rows={1} placeholder="Enter remarks or additional notes (optional)…"
                                    value={form.InvoiceRemarks}
                                    onChange={e => setForm(p => ({ ...p, InvoiceRemarks: e.target.value }))}
                                    disabled={isBusy} className={`${inputCls} resize-y`} />
                            </div>
                        </div>
                    </div>

                    {/* Trading items */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={Package} title="Trading Items" />
                        <div className="p-4 space-y-3">

                            {/* Item selector + Add button */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end p-3 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg border border-indigo-100 dark:border-indigo-800">
                                <div className="md:col-span-3">
                                    <Label>Trade Item</Label>
                                    <div className="relative">
                                        <select value={selItem} onChange={e => handleItemSelect(e.target.value)}
                                            disabled={!form.CCCode || itemsLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                            <option value="">{!form.CCCode ? 'Select CC first' : itemsLoading ? 'Loading…' : '— Select Item —'}</option>
                                            {itemOptions.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                                        </select>
                                        <SelectIcon loading={itemsLoading} />
                                    </div>
                                </div>
                                <button type="button" onClick={handleAddToInvoice}
                                    disabled={!selItem || !tempDetails.length || tempSaveLoading || isBusy}
                                    className="py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50 transition-colors">
                                    {tempSaveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                    Add to Invoice
                                </button>
                            </div>

                            {/* Temp details table */}
                            {tempLoading && (
                                <div className="flex items-center justify-center py-3">
                                    <Loader2 className="h-4 w-4 text-indigo-500 animate-spin mr-2" />
                                    <span className="text-xs text-gray-500">Loading temp issue details…</span>
                                </div>
                            )}

                            {!tempLoading && tempDetails.length > 0 && (
                                <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-800">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-indigo-50 dark:bg-indigo-900/30">
                                                <th className={`${thCls} text-left`}>#</th>
                                                <th className={`${thCls} text-left`}>Date</th>
                                                <th className={`${thCls} text-left`}>Item Code</th>
                                                <th className={`${thCls} text-left`}>MRR No</th>
                                                <th className={`${thCls} text-left`}>PO No</th>
                                                <th className={`${thCls} text-right`}>Avail Qty</th>
                                                <th className={`${thCls} text-right`}>Issue Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-indigo-100 dark:divide-indigo-800">
                                            {tempDetails.map((row, idx) => {
                                                const rowId = row.TempId ?? idx;
                                                return (
                                                    <tr key={rowId} className="hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors">
                                                        <td className={`${tdCls} text-gray-500`}>{idx + 1}</td>
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.Tempdate || ''}</td>
                                                        <td className={`${tdCls} font-medium text-gray-800 dark:text-gray-100`}>{row.TempItemcode || ''}</td>
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.Tempmrr || ''}</td>
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{row.Temppono || ''}</td>
                                                        <td className={`${tdCls} text-right text-gray-600 dark:text-gray-300`}>{fmt(row.TempQuantity || 0)}</td>
                                                        <td className={`${tdCls} text-right`}>
                                                            <input type="number" min="0" max={parseFloat(row.TempQuantity || 0)} placeholder="0"
                                                                value={tempQtys[rowId] || ''}
                                                                onChange={e => setTempQtys(p => ({ ...p, [rowId]: e.target.value }))}
                                                                className={cellInput('w-24 border-indigo-200 dark:border-indigo-700')} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Invoice items grid — editable rate, HSN, GST% */}
                            {gridLoading && (
                                <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
                                    <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> Loading items…
                                </div>
                            )}

                            {tradeRows.length > 0 && (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-sm min-w-[760px]">
                                        <thead>
                                            <tr className="bg-gray-50 dark:bg-gray-900/40">
                                                <th className={`${thCls} text-left`}>#</th>
                                                <th className={`${thCls} text-left`}>Item</th>
                                                <th className={`${thCls} text-right`}>Qty</th>
                                                <th className={`${thCls} text-right`}>Rate</th>
                                                <th className={`${thCls} text-right`}>Amount</th>
                                                <th className={`${thCls} text-left`}>HSN</th>
                                                <th className={`${thCls} text-right`}>CGST%</th>
                                                <th className={`${thCls} text-right`}>SGST%</th>
                                                <th className={`${thCls} text-right`}>IGST%</th>
                                                <th className={`${thCls} text-center`}></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {tradeRows.map((row, idx) => (
                                                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-colors">
                                                    <td className={`${tdCls} text-gray-500`}>{idx + 1}</td>
                                                    <td className={`${tdCls} max-w-[220px]`}>
                                                        <p className="font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.itemCode}</p>
                                                        <p className="text-xs text-gray-600 dark:text-gray-300 truncate" title={row.itemName}>{row.itemName}</p>
                                                        {row.spec && <p className="text-[11px] text-gray-400 truncate" title={row.spec}>{row.spec}</p>}
                                                    </td>
                                                    <td className={`${tdCls} text-right font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap`}>
                                                        {row.qty} <span className="text-[11px] text-gray-400">{row.unit}</span>
                                                    </td>
                                                    <td className={`${tdCls} text-right`}>
                                                        <input type="number" min="0" step="0.01" placeholder="0.00" value={row.rate}
                                                            onChange={e => updateRow(row.id, 'rate', e.target.value)}
                                                            className={cellInput('w-24')} />
                                                    </td>
                                                    <td className={`${tdCls} text-right font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap`}>
                                                        {fmt(rowAmount(row))}
                                                    </td>
                                                    <td className={tdCls}>
                                                        <input type="text" placeholder="HSN…" value={row.hsn}
                                                            onChange={e => updateRow(row.id, 'hsn', e.target.value)}
                                                            className={cellInput('w-20 text-left')} />
                                                    </td>
                                                    <td className={`${tdCls} text-right`}>
                                                        <input type="number" min="0" max="100" placeholder="0" value={row.cgstPer}
                                                            onChange={e => updateRow(row.id, 'cgstPer', e.target.value)}
                                                            className={cellInput('w-14')} />
                                                    </td>
                                                    <td className={`${tdCls} text-right`}>
                                                        <input type="number" min="0" max="100" placeholder="0" value={row.sgstPer}
                                                            onChange={e => updateRow(row.id, 'sgstPer', e.target.value)}
                                                            className={cellInput('w-14')} />
                                                    </td>
                                                    <td className={`${tdCls} text-right`}>
                                                        <input type="number" min="0" max="100" placeholder="0" value={row.igstPer}
                                                            onChange={e => updateRow(row.id, 'igstPer', e.target.value)}
                                                            className={cellInput('w-14')} />
                                                    </td>
                                                    <td className={`${tdCls} text-center`}>
                                                        <button type="button" onClick={() => handleRemoveRow(row)} disabled={deleteLoading}
                                                            className="p-1 rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-50">
                                                            {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-indigo-50/60 dark:bg-indigo-900/20">
                                                <td colSpan={4} className={`${tdCls} text-right text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase`}>Basic Value Total</td>
                                                <td className={`${tdCls} text-right text-sm font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap`}>{fmt(basicValue)}</td>
                                                <td colSpan={5} />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tax — GST and TCS side by side */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={ShieldCheck} title="Tax Configuration" />
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700">

                            {/* GST — amounts auto-computed from item GST percentages */}
                            <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                        <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" /> GST Applicable?<span className="text-rose-500">*</span>
                                    </span>
                                    <YesNoToggle value={form.IsGstApplicable} onChange={v => setForm(p => ({ ...p, IsGstApplicable: v }))} />
                                </div>

                                {form.IsGstApplicable === 'Yes' && (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label required>Client GST</Label>
                                                <div className="relative">
                                                    <select value={form.ClientGST}
                                                        onChange={e => {
                                                            const opt = clientGSTOptions.find(g => g.value === e.target.value);
                                                            setForm(p => ({ ...p, ClientGST: e.target.value, TaxId: opt?.taxId || 0 }));
                                                        }}
                                                        disabled={gstLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                                        <option value="">{gstLoading ? 'Loading…' : '— Select —'}</option>
                                                        {clientGSTOptions.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                                                    </select>
                                                    <SelectIcon loading={gstLoading} />
                                                </div>
                                            </div>
                                            <div>
                                                <Label required>Company GST</Label>
                                                <div className="relative">
                                                    <select value={form.CompanyGST}
                                                        onChange={e => setForm(p => ({ ...p, CompanyGST: e.target.value }))}
                                                        disabled={gstLoading || isBusy} className={`${inputCls} appearance-none pr-8`}>
                                                        <option value="">{gstLoading ? 'Loading…' : '— Select —'}</option>
                                                        {companyGSTOptions.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                                                    </select>
                                                    <SelectIcon loading={gstLoading} />
                                                </div>
                                            </div>
                                        </div>

                                        {gstDetected && (
                                            <>
                                                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                                                    form.Statecheck
                                                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
                                                        : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                                                }`}>
                                                    <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                                    {form.Statecheck ? 'Same State — CGST + SGST applicable' : 'Inter-State — IGST applicable'}
                                                </div>

                                                {form.Statecheck ? (
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <Label>CGST DCA Code</Label>
                                                            <input type="text" placeholder="CGST DCA…" value={form.CgstDca}
                                                                onChange={e => setForm(p => ({ ...p, CgstDca: e.target.value }))}
                                                                disabled={isBusy} className={inputCls} />
                                                            <p className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold">From items: ₹ {fmt(totalCgst)}</p>
                                                        </div>
                                                        <div>
                                                            <Label>SGST DCA Code</Label>
                                                            <input type="text" placeholder="SGST DCA…" value={form.SgstDca}
                                                                onChange={e => setForm(p => ({ ...p, SgstDca: e.target.value }))}
                                                                disabled={isBusy} className={inputCls} />
                                                            <p className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold">From items: ₹ {fmt(totalSgst)}</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="sm:w-1/2">
                                                        <Label>IGST DCA Code</Label>
                                                        <input type="text" placeholder="IGST DCA…" value={form.IgstDca}
                                                            onChange={e => setForm(p => ({ ...p, IgstDca: e.target.value }))}
                                                            disabled={isBusy} className={inputCls} />
                                                        <p className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold">From items: ₹ {fmt(totalIgst)}</p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* TCS */}
                            <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                        <Percent className="w-3.5 h-3.5 text-indigo-500" /> TCS Applicable?<span className="text-rose-500">*</span>
                                    </span>
                                    <YesNoToggle value={form.IsTCSApplicable} onChange={v => setForm(p => ({ ...p, IsTCSApplicable: v }))} />
                                </div>
                                {form.IsTCSApplicable === 'Yes' && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>TCS DCA</Label>
                                            <input type="text" value={form.TCSDCA}
                                                onChange={e => setForm(p => ({ ...p, TCSDCA: e.target.value }))}
                                                disabled={isBusy} className={inputCls} />
                                        </div>
                                        <div>
                                            <Label>TCS SDCA</Label>
                                            <input type="text" value={form.TCSSDCA}
                                                onChange={e => setForm(p => ({ ...p, TCSSDCA: e.target.value }))}
                                                disabled={isBusy} className={inputCls} />
                                        </div>
                                        <div className="col-span-2">
                                            <Label>TCS Amount</Label>
                                            <RupeeInput value={form.TCSAmount} onChange={e => setForm(p => ({ ...p, TCSAmount: e.target.value }))} disabled={isBusy} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Invoice Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                {[
                                    { label: 'Invoice Type', value: form.InvoiceType },
                                    { label: 'Cost Center',  value: form.CCCode },
                                    { label: 'Client',       value: clientLabel },
                                    { label: 'Sub Client',   value: form.SubClientcode },
                                    { label: 'PO Number',    value: form.PONumber },
                                    { label: 'Invoice No.',  value: form.ClientInvoiceNo },
                                    { label: 'Invoice Date', value: formatDateForAPI(form.InvoiceDate) },
                                    { label: 'Items',        value: tradeRows.length ? String(tradeRows.length) : '' },
                                ].map(({ label, value }) => (
                                    <div key={label} className="flex justify-between gap-3">
                                        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
                                        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
                                    </div>
                                ))}
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">Basic Value</dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">₹{fmt(basicValue)}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">
                                        GST{form.IsGstApplicable === 'Yes' && gstDetected ? (form.Statecheck ? ' (CGST+SGST)' : ' (IGST)') : ''}
                                    </dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">
                                        {form.IsGstApplicable === 'Yes' ? `₹${fmt(gstAmt)}` : (form.IsGstApplicable || '—')}
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">TCS</dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">
                                        {form.IsTCSApplicable === 'Yes' ? `₹${fmt(tcsAmt)}` : (form.IsTCSApplicable || '—')}
                                    </dd>
                                </div>
                            </dl>

                            <div className="px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                                <p className="text-indigo-100 text-[10px] font-semibold uppercase tracking-wider">Total Invoice Value</p>
                                <p className="text-2xl font-black">₹ {fmt(invoiceTotal)}</p>
                            </div>

                            <div className="flex gap-2">
                                <button type="button" onClick={handleReset} disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={isBusy}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                                        : <><Send className="h-4 w-4" /> Submit Invoice</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientTradingInvoiceCreation;
